import { Children, cloneElement, useCallback } from 'react';
import PropTypes from 'prop-types';

import { setRef } from 'ravenjs/utils/component';

/**
 * Forwards a ref to the DOM node of a single child element.
 *
 * Replaces the previous `findDOMNode(this)` implementation: the child now
 * receives a forked `ref` that resolves to its underlying DOM node, which is
 * relayed to `rootRef` (and merged with any existing ref on the child). The
 * child must be able to hold a ref (a host element or a `forwardRef`/styled
 * component).
 *
 * @method RootRef
 * @param  {Object} props The component props
 * @return {JSX}
 */
function RootRef({ children, rootRef }) {
    const child = Children.only(children);
    const childRef = child.ref;

    const handleRef = useCallback(
        node => {
            setRef(rootRef, node);
            setRef(childRef, node);
        },
        [rootRef, childRef]
    );

    return cloneElement(child, { ref: handleRef });
}

RootRef.propTypes = {
    /**
     * The wrapped React element.
     */
    children: PropTypes.element.isRequired,
    /**
     * The function / object to access the DOM node of the wrapped element.
     * Can be a function or a `React.createRef()`.
     */
    rootRef: PropTypes.oneOfType([PropTypes.object, PropTypes.func]).isRequired,
};

export default RootRef;
